import { type MRT_ColumnDef } from "material-react-table";
import { useMemo } from "react";
import {
  cellNotFilter,
  emptyCellOneLevel,
  hasPermission,
  texto,
} from "@/shared";
import { Flota } from "@/shared/interfaces/erp/mantenimiento-operaciones";
import { SwitchComponente } from "@/shared/components/ui";
import { PermissionsEnum } from "@/auth/interfaces";
import { useUiConfirmModalStore, useUiStore } from "@/store/ui";
import { AlertSeverityEnum } from "@/shared/interfaces";
import { useUpdateFlota } from "@/shared/actions/erp";

type FlotasColumnsParams = {
  enableStateSwitch?: boolean;
};

export const useTableColumnsFlotas = ({
  enableStateSwitch = true,
}: FlotasColumnsParams) => {
  ///* global state -----------------------
  const setConfirmDialog = useUiConfirmModalStore((s) => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    (s) => s.setConfirmDialogIsOpen
  );
  const setAlert = useUiStore((s) => s.setAlert);

  ///* mutations -----------------------
  const updSate = useUpdateFlota();

  const columnsFlota = useMemo<MRT_ColumnDef<Flota>[]>(
    () => [
      {
        accessorKey: "nombre",
        header: "Nombre",
        Cell: ({ row }) => emptyCellOneLevel(row, "nombre"),
      },
      {
        accessorKey: "lider_razon_social",
        header: "Líder",
        Cell: ({ row }) => row.original.lider_data?.razon_social,
      },
      {
        accessorKey: "auxiliar",
        header: "Auxiliar",
        Cell: ({ row }) => emptyCellOneLevel(row, "auxiliar"),
      },
      {
        accessorKey: "estado",
        header: "Activo",
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          return (
            <SwitchComponente
              estadoSwitch={row.original?.estado}
              titulo={row.original?.estado ? "Activo" : "Inactivo"}
              setEstadoSwitch={(state) => {
                if (
                  !hasPermission(PermissionsEnum.api_change_flota) ||
                  !enableStateSwitch
                )
                  return setAlert(
                    true,
                    texto.acceso_denegado_403,
                    AlertSeverityEnum.error
                  );

                setConfirmDialog({
                  isOpen: true,
                  title: "Cambiar Estado",
                  subtitle:
                    "¿Está seguro que desea cambiar el estado de este registro ?",
                  onConfirm: () => {
                    setConfirmDialogIsOpen(false);
                    updSate.mutate({
                      id: row.original?.id_flota!,
                      data: { estado: !!state } as any,
                    });
                  },
                });
              }}
            />
          );
        },
      },
      {
        accessorKey: "telefono_1",
        header: "Teléfono 1",
        Cell: ({ row }) => emptyCellOneLevel(row, "telefono_1"),
      },
      {
        accessorKey: "telefono_2",
        header: "Teléfono 2",
        Cell: ({ row }) => emptyCellOneLevel(row, "telefono_2"),
      },
      {
        accessorKey: "telefono_3",
        header: "Teléfono 3",
        Cell: ({ row }) => emptyCellOneLevel(row, "telefono_3"),
      },
      {
        ...cellNotFilter,
        accessorKey: "email",
        header: "Email",
        Cell: ({ row }) => emptyCellOneLevel(row, "email"),
      },
      {
        accessorKey: "marca_vehiculo",
        header: "Modelo",
        Cell: ({ row }) => emptyCellOneLevel(row, "marca_vehiculo"),
      },
      {
        accessorKey: "modelo_vehiculo",
        header: "Marca",
        size: 162,
        Cell: ({ row }) => emptyCellOneLevel(row, "modelo_vehiculo"),
      },
      {
        accessorKey: "placa_vehiculo",
        header: "Placa",
        size: 162,
        Cell: ({ row }) => emptyCellOneLevel(row, "placa_vehiculo"),
      },
      {
        accessorKey: "anio_vehiculo",
        header: "Año",
        size: 162,
        Cell: ({ row }) => emptyCellOneLevel(row, "anio_vehiculo"),
      },
      {
        ...cellNotFilter,
        accessorKey: "color_vehiculo",
        header: "Color",
        size: 162,
        Cell: ({ row }) => emptyCellOneLevel(row, "color_vehiculo"),
      },

      ///* fk
      {
        ...cellNotFilter,
        accessorKey: "provincia",
        header: "Provincia",
        size: 300,
        Cell: ({ row }) => row.original?.provincia_data?.nombre || "N/A",
      },
      {
        ...cellNotFilter,
        accessorKey: "ciudad",
        header: "Ciudad",
        Cell: ({ row }) => row.original?.ciudad_data?.nombre || "N/A",
      },
      {
        ...cellNotFilter,
        accessorKey: "zona",
        header: "Zona",
        Cell: ({ row }) => row.original?.zona_data?.nombre || "N/A",
      },
    ],
    []
  );

  const columnsPlanificador = useMemo<MRT_ColumnDef<Flota>[]>(
    () => [
      {
        accessorKey: "nombre",
        header: "Nombre",
        Cell: ({ row }) => emptyCellOneLevel(row, "nombre"),
      },
      {
        accessorKey: "lider_razon_social",
        header: "Líder",
        Cell: ({ row }) => row.original.lider_data?.razon_social,
      },
      {
        accessorKey: "auxiliar",
        header: "Auxiliar",
        Cell: ({ row }) => emptyCellOneLevel(row, "auxiliar"),
      },

      {
        accessorKey: "telefono_1",
        header: "Teléfono 1",
        Cell: ({ row }) => emptyCellOneLevel(row, "telefono_1"),
      },
      {
        accessorKey: "telefono_2",
        header: "Teléfono 2",
        Cell: ({ row }) => emptyCellOneLevel(row, "telefono_2"),
      },
      {
        accessorKey: "telefono_3",
        header: "Teléfono 3",
        Cell: ({ row }) => emptyCellOneLevel(row, "telefono_3"),
      },
      {
        ...cellNotFilter,
        accessorKey: "email",
        header: "Email",
        Cell: ({ row }) => emptyCellOneLevel(row, "email"),
      },
      {
        accessorKey: "marca_vehiculo",
        header: "Modelo",
        Cell: ({ row }) => emptyCellOneLevel(row, "marca_vehiculo"),
      },
      {
        accessorKey: "modelo_vehiculo",
        header: "Marca",
        size: 162,
        Cell: ({ row }) => emptyCellOneLevel(row, "modelo_vehiculo"),
      },
      {
        accessorKey: "placa_vehiculo",
        header: "Placa",
        size: 162,
        Cell: ({ row }) => emptyCellOneLevel(row, "placa_vehiculo"),
      },
      {
        accessorKey: "anio_vehiculo",
        header: "Año",
        size: 162,
        Cell: ({ row }) => emptyCellOneLevel(row, "anio_vehiculo"),
      },
      {
        ...cellNotFilter,
        accessorKey: "color_vehiculo",
        header: "Color",
        size: 162,
        Cell: ({ row }) => emptyCellOneLevel(row, "color_vehiculo"),
      },

      ///* fk
      {
        ...cellNotFilter,
        accessorKey: "provincia",
        header: "Provincia",
        size: 300,
        Cell: ({ row }) => row.original?.provincia_data?.nombre || "N/A",
      },
      {
        ...cellNotFilter,
        accessorKey: "ciudad",
        header: "Ciudad",
        Cell: ({ row }) => row.original?.ciudad_data?.nombre || "N/A",
      },
      {
        ...cellNotFilter,
        accessorKey: "zona",
        header: "Zona",
        Cell: ({ row }) => row.original?.zona_data?.nombre || "N/A",
      },
    ],
    []
  );

  const columnsPlanificadorView = useMemo<MRT_ColumnDef<Flota>[]>(
    () => [
      {
        accessorKey: "nombre",
        header: "Nombre",
        Cell: ({ row }) => emptyCellOneLevel(row, "nombre"),
      },

      ///* fk
      {
        accessorKey: "provincia",
        header: "Provincia",
        Cell: ({ row }) => row.original?.provincia_data?.nombre || "N/A",
      },
      {
        accessorKey: "ciudad",
        header: "Ciudad",
        Cell: ({ row }) => row.original?.ciudad_data?.nombre || "N/A",
      },
      {
        accessorKey: "zona",
        header: "Zona",
        Cell: ({ row }) => row.original?.zona_data?.nombre || "N/A",
      },
      {
        accessorKey: "lider_razon_social",
        header: "Líder",
        Cell: ({ row }) => row.original.lider_data?.razon_social,
      },
      {
        accessorKey: "auxiliar",
        header: "Auxiliar",
        Cell: ({ row }) => emptyCellOneLevel(row, "auxiliar"),
      },
    ],
    []
  );

  return {
    columnsFlota,
    columnsPlanificador,
    columnsPlanificadorView,
  };
};
